import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  api,
  ApiError,
  AnswerResult,
  CoverageTree,
  DistractorRationales,
  RichRationale,
  ServedQuestion,
  VoteCounts,
} from "../lib/api";
import { buildSearchIndex, MatchResult, search, SearchItem } from "../lib/scope-search";
import { renderQuestionContent, WorkedSolutionText } from "../lib/render-question";
import {
  composeMultiPartAnswer,
  multiPartReady,
  parseMultiPartStem,
  ParsedMultiPart,
} from "../lib/multiPart";
import { useAuth } from "../lib/auth-context";

type Mode = "loading" | "answering" | "graded" | "empty";

interface SessionState {
  seen: number[];                  // qids served this session
  recentSubSkills: string[];       // last few sub_skill_ids to avoid repeating
}

interface Scope {
  topic_slug?: string;
  concept_id?: string;
}

interface SubjectOption {
  slug: string;
  name: string;
}

// Student-facing question-format filter. Default "all" = mixed (no filter).
// The filter selection is the product's main demand signal for where to
// point generation spend — see the format_filter_changed event for the
// per-selection log. Presentation MUST stay neutral so the data is honest.
type FormatFilter = "all" | "mc" | "fill_in";

interface FormatOption {
  value: FormatFilter;
  label: string;
}

// Order here is the on-screen order. "All" first so the default is the
// leftmost chip (no implied second-class status); MC and Fill-in are listed
// in a neutral order — no recommendation, no bolding, no description text
// nudging students toward one.
const FORMAT_OPTIONS: FormatOption[] = [
  { value: "all", label: "All formats" },
  { value: "mc", label: "Multiple choice" },
  { value: "fill_in", label: "Fill in the answer" },
];

export default function Practice() {
  const { me } = useAuth();
  const [mode, setMode] = useState<Mode>("loading");
  const [q, setQ] = useState<ServedQuestion | null>(null);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [draft, setDraft] = useState<string>("");
  const [chosenLetter, setChosenLetter] = useState<string | null>(null);
  const [partAnswers, setPartAnswers] = useState<string[]>([]);
  const [votes, setVotes] = useState<VoteCounts>({ upvotes: 0, downvotes: 0, my_vote: 0 });
  const [scope, setScope] = useState<Scope>({});
  const [format, setFormat] = useState<FormatFilter>("all");
  const [tree, setTree] = useState<CoverageTree | null>(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentStatus, setCommentStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [dailyCapMessage, setDailyCapMessage] = useState<string | null>(null);

  const session = useRef<SessionState>({ seen: [], recentSubSkills: [] });
  const tStarted = useRef<number>(Date.now());

  // Parse a multi_part stem into per-part labels + inferred types so the
  // input UI can render structured inputs.
  //
  // Two paths:
  //   1. Flavor 2 — `q.parts` is present on the served payload. The
  //      generator authored typed parts and the server stored them as
  //      JSON; we synthesize a ParsedMultiPart directly from that array
  //      so the existing rendering code keeps working unchanged. Types
  //      land as `known` confidence because they were declared, not
  //      inferred.
  //   2. Legacy — `q.parts` is null. Parse the stem's (a)/(b)/(c)/(d)
  //      markers as before. Questions without markers fall through to
  //      the single-textarea fallback further down (no regression).
  const parsedParts = useMemo<ParsedMultiPart | null>(() => {
    if (!q || q.response_format !== "multi_part") return null;
    if (q.parts && q.parts.length > 0) {
      return {
        preamble: q.question,
        parts: q.parts.map((p) => {
          if (p.type === "numeric") {
            return {
              letter: p.label,
              prompt: p.prompt,
              type: { kind: "numeric" as const },
              confidence: "known" as const,
            };
          }
          if (p.type === "choice") {
            // The frontend's existing renderer treats binary as two buttons;
            // for 2-4 choices we synthesize the same shape and the
            // multi-part renderer below has a generalized fallthrough path.
            const opts = (p.choices ?? []).slice(0, 4);
            return {
              letter: p.label,
              prompt: p.prompt,
              type: { kind: "choice" as const, options: opts },
              confidence: "known" as const,
            };
          }
          return {
            letter: p.label,
            prompt: p.prompt,
            type: { kind: "text" as const },
            confidence: "known" as const,
          };
        }),
      };
    }
    return parseMultiPartStem(q.question);
  }, [q]);

  // Size the partAnswers array to the parsed parts (or [""] if unparsed)
  // when a new question lands. We do this in an effect rather than at
  // fetchNext time because parsedParts depends on the question state.
  useEffect(() => {
    if (parsedParts) {
      setPartAnswers(new Array(parsedParts.parts.length).fill(""));
    } else if (q?.response_format === "multi_part") {
      setPartAnswers([""]);
    }
  }, [parsedParts, q?.id, q?.response_format]);

  // Load coverage tree once (used by the optional scope picker).
  useEffect(() => {
    api.coverageTree().then(setTree).catch(() => setTree(null));
  }, []);

  const fetchNext = useCallback(async () => {
    setMode("loading");
    setQ(null);
    setResult(null);
    setDraft("");
    setChosenLetter(null);
    setPartAnswers([]);
    setShowCommentBox(false);
    setCommentDraft("");
    setCommentStatus("idle");
    try {
      const next = await api.next({
        topic_slug: scope.topic_slug,
        concept_id: scope.concept_id,
        format: format === "all" ? undefined : format,
        exclude_ids: session.current.seen,
        exclude_recent_sub_skills: session.current.recentSubSkills.slice(-3),
      });
      session.current.seen.push(next.id);
      if (next.sub_skill_id) session.current.recentSubSkills.push(next.sub_skill_id);
      const v = await api.voteCounts(next.id);
      setQ(next);
      setVotes(v);
      setMode("answering");
      tStarted.current = Date.now();
      // multi_part: best-effort, prefill an empty array sized to the answer key.
      // We don't know the part count until we have the answer; fall back to 1.
      setPartAnswers([""]);
    } catch (err: any) {
      if (err?.status === 404) {
        setMode("empty");
      } else {
        setMode("answering");
      }
    }
  }, [scope.topic_slug, scope.concept_id, format]);

  useEffect(() => {
    fetchNext();
  }, [fetchNext]);

  if (!me) return null;
  if (!me.signed_in) return <Navigate to="/login" replace />;
  if (!me.access_code_verified) return <Navigate to="/access-code" replace />;

  if (mode === "loading") {
    return (
      <div className="page">
        <p className="muted">Loading a verified question…</p>
      </div>
    );
  }

  if (mode === "empty" || !q) {
    return (
      <div className="page">
        <h1 className="h1">You&rsquo;ve seen them all.</h1>
        <p className="lede">No more candidate questions match this scope. Try a different topic.</p>
      </div>
    );
  }

  async function commitAnswer() {
    if (!q) return;
    const hasStructured = !!(q.parts && q.parts.length > 0);
    const ans = composeAnswer(
      q.response_format, parsedParts,
      { draft, chosenLetter, partAnswers },
      hasStructured,
    );
    if (!ans) return;
    const tMs = Date.now() - tStarted.current;
    try {
      const r = await api.answer(q.id, ans, tMs);
      setResult(r);
      setMode("graded");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        // Per-user daily answer cap. Surface friendly inline copy; keep the
        // student on the current question (no grade, no skip recorded).
        const detail = (err.detail as { detail?: string } | null)?.detail;
        setDailyCapMessage(
          detail ?? "You've hit today's practice limit. Try again tomorrow.",
        );
        return;
      }
      throw err;
    }
  }

  async function castVote(value: -1 | 1) {
    if (!q) return;
    const next = value === votes.my_vote ? 0 : value;
    const r = await api.vote(q.id, next as -1 | 0 | 1);
    setVotes({ ...r, my_vote: next });
  }

  async function postComment() {
    if (!q || !commentDraft.trim()) return;
    setCommentStatus("sending");
    try {
      await api.comment(q.id, commentDraft.trim());
      setCommentStatus("sent");
      setCommentDraft("");
    } catch {
      setCommentStatus("idle");
    }
  }

  async function skip() {
    if (!q) return;
    const tMs = Date.now() - tStarted.current;
    await api.skip(q.id, tMs);
    fetchNext();
  }

  return (
    <div className="page">
      <div className="stack-lg">
        {tree && (
          <ScopePicker
            tree={tree}
            scope={scope}
            onChange={(s, meta) => {
              // Resetting the scope wipes the session-local seen+recent buffers
              // so the new scope can serve from scratch (otherwise we might 404
              // on a narrow scope due to stale exclusions).
              session.current = { seen: [], recentSubSkills: [] };
              setScope(s);
              // scope_changed payload includes the raw typed query when the
              // selection came from the search box. This is the data the
              // post-pilot semantic matcher will train on — how students
              // describe what they actually want.
              const desc = describeScope(tree, s);
              api
                .event("scope_changed", {
                  payload: {
                    topic_slug: s.topic_slug ?? null,
                    concept_id: s.concept_id ?? null,
                    label: desc.name,
                    source: meta.source,
                    query: meta.query ?? null,
                  },
                })
                .catch(() => {});
            }}
            onSearchTyped={(q) => {
              // Fired (debounced ~600ms) whenever the student types in the
              // search box. Captures intent even when they don't select —
              // "what they tried to type" vs "what they finally picked".
              api.event("scope_search", { payload: { query: q } }).catch(() => {});
            }}
          />
        )}

        <FormatBar
          value={format}
          onChange={(f) => {
            if (f === format) return;
            session.current = { seen: [], recentSubSkills: [] };
            setFormat(f);
            // The whole point of the FORMAT filter is demand-signal capture.
            // Every selection lands in events tagged with the student's
            // current topic scope + their blazerid (auto via emit() lookup)
            // so post-pilot we can answer "which formats do students choose,
            // for which topics, how often" → generation budget decisions.
            api
              .event("format_filter_changed", {
                payload: {
                  format: f,
                  topic_slug: scope.topic_slug ?? null,
                  concept_id: scope.concept_id ?? null,
                },
              })
              .catch(() => {});
          }}
        />

        <div className="card stack">
          <div className="question-card__meta">
            <span
              className="question-card__concept"
              title={`Concept: ${q.node_canonical_name}`}
            >
              {/* Prefer the short student-facing display_label; fall back
                  to the syllabus learning-objective canonical_name when
                  no display_label has been authored. The tooltip always
                  carries canonical_name so the underlying concept is
                  still visible on hover. */}
              {q.node_display_label || q.node_canonical_name}
            </span>
            <span
              className="question-card__difficulty"
              title={`Difficulty: ${q.difficulty}`}
            >
              {q.difficulty}
            </span>
          </div>
          <div className="question-text">
            {renderQuestionContent(
              // When we render structured per-part inputs below, the question
              // card shows only the preamble — the (a)/(b)/(c)/(d) prompts
              // become part labels and would otherwise duplicate.
              parsedParts ? parsedParts.preamble : q.question,
            )}
          </div>

          <AnswerInput
            q={q}
            parsedParts={parsedParts}
            disabled={mode === "graded"}
            draft={draft}
            setDraft={setDraft}
            chosenLetter={chosenLetter}
            setChosenLetter={setChosenLetter}
            partAnswers={partAnswers}
            setPartAnswers={setPartAnswers}
            graded={mode === "graded" ? result : null}
          />

          {mode === "answering" && (
            <div className="controls">
              <button
                className="btn"
                disabled={
                  !!dailyCapMessage ||
                  !canCommit(q.response_format, parsedParts, { draft, chosenLetter, partAnswers })
                }
                onClick={commitAnswer}
              >
                Submit answer
              </button>
              <button
                className="btn btn--ghost"
                disabled={!!dailyCapMessage}
                onClick={skip}
              >
                Skip
              </button>
            </div>
          )}

          {dailyCapMessage && (
            <div
              className="feedback-banner"
              style={{
                background: "rgba(0,0,0,0.04)",
                borderRadius: 6,
                padding: "10px 12px",
              }}
            >
              {dailyCapMessage} Practice resets tomorrow — come back then.
            </div>
          )}

          {mode === "graded" && result && (
            <>
              <div
                className={
                  "feedback-banner " +
                  (result.method === "free_text_review"
                    ? ""
                    : result.correct
                    ? "feedback-banner--correct"
                    : "feedback-banner--incorrect")
                }
              >
                {result.method === "free_text_review"
                  ? "Open response: review the worked solution below."
                  : result.correct
                  ? "Correct."
                  : "Not quite. Here’s how it works out."}
              </div>

              <Solution result={result} q={q} />

              <div className="controls">
                <button className="btn" onClick={fetchNext}>
                  Next question
                </button>
                <button className="btn btn--ghost" onClick={fetchNext}>
                  Another like this →
                </button>
                <span className="spacer" />
                <div className="vote-pair" role="group" aria-label="Rate this question">
                  <button
                    aria-pressed={votes.my_vote === 1}
                    onClick={() => castVote(1)}
                    title="Helpful question"
                  >
                    ▲ {votes.upvotes}
                  </button>
                  <button
                    aria-pressed={votes.my_vote === -1}
                    onClick={() => castVote(-1)}
                    title="Something’s off"
                  >
                    ▼ {votes.downvotes}
                  </button>
                </div>
                <button className="btn btn--ghost" onClick={() => setShowCommentBox((x) => !x)}>
                  Comment
                </button>
              </div>

              {showCommentBox && (
                <div className="stack">
                  <textarea
                    className="input"
                    rows={3}
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Anything off about this question? (Visible only to your professor.)"
                    disabled={commentStatus === "sending" || commentStatus === "sent"}
                  />
                  <div className="row">
                    <button
                      className="btn"
                      onClick={postComment}
                      disabled={!commentDraft.trim() || commentStatus !== "idle"}
                    >
                      {commentStatus === "sent" ? "Sent ✓" : commentStatus === "sending" ? "Sending…" : "Send"}
                    </button>
                    <button className="btn btn--ghost" onClick={() => setShowCommentBox(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function findTopicForConcept(tree: CoverageTree, conceptId: string) {
  for (const t of tree.topics) {
    if (t.concepts.some((c) => c.concept_id === conceptId)) return t;
  }
  return null;
}

function findConcept(tree: CoverageTree, conceptId: string) {
  for (const t of tree.topics) {
    const c = t.concepts.find((x) => x.concept_id === conceptId);
    if (c) return c;
  }
  return null;
}

function describeScope(tree: CoverageTree, scope: Scope) {
  if (scope.concept_id) {
    const c = findConcept(tree, scope.concept_id);
    const t = findTopicForConcept(tree, scope.concept_id);
    return {
      name: c?.name ?? scope.concept_id,
      crumb: t?.name ?? null,
      count: c?.n_questions,
    };
  }
  if (scope.topic_slug) {
    const t = tree.topics.find((x) => x.slug === scope.topic_slug);
    return { name: t?.name ?? scope.topic_slug, crumb: null, count: t?.n_questions };
  }
  return { name: "All topics", crumb: null, count: tree.topics.reduce((n, t) => n + t.n_questions, 0) };
}

type ScopeChangeMeta = { query?: string; source: "search" | "browse" | "clear" };

type DisplayRow =
  | { kind: "group-topic"; item: SearchItem; groupId: string }
  | { kind: "group-child"; item: SearchItem; groupId: string }
  | { kind: "section-header"; label: string }
  | { kind: "standalone"; item: SearchItem };

// Subjects available to the student. For the pilot there is exactly one
// (AC 401 Exam 1). The list is derived from the tree's subject + an
// optional dev override (`?subjects=multi` on the URL) that injects a
// second synthetic subject so the multi-subject picker UX is visually
// verifiable without backend changes. When a subjects endpoint lands, the
// override goes away and the source becomes a real API call.
// Student-facing subject names live here, keyed off the backend's subject
// slug. The backend's stored `subject.name` is course/exam shaped
// ("AC 401 Exam 1") — that's a course artifact, not a discipline. Students
// should see the discipline ("Cost Accounting"). When the real
// `/api/subjects` endpoint lands and returns discipline names directly,
// this map goes away.
const SUBJECT_DISPLAY_NAMES: Record<string, string> = {
  exam1: "Cost Accounting",
};

function displayNameForSubject(slug: string, fallback: string): string {
  return SUBJECT_DISPLAY_NAMES[slug] ?? fallback;
}

function useSubjects(tree: CoverageTree): SubjectOption[] {
  return useMemo<SubjectOption[]>(() => {
    const primary = {
      slug: tree.subject.slug,
      name: displayNameForSubject(tree.subject.slug, tree.subject.name),
    };
    if (typeof window === "undefined") return [primary];
    const params = new URLSearchParams(window.location.search);
    if (params.get("subjects") === "multi") {
      return [
        primary,
        { slug: "demo.fin_acct", name: "Financial Accounting (preview)" },
      ];
    }
    return [primary];
  }, [tree.subject.slug, tree.subject.name]);
}

// Always-visible chip row. Equal visual weight on every option — this picker
// IS the demand sensor, so any UI nudging toward MC (or any other format)
// would corrupt the signal we're collecting.
function FormatBar({
  value,
  onChange,
}: {
  value: FormatFilter;
  onChange: (v: FormatFilter) => void;
}) {
  return (
    <div className="format-bar" role="radiogroup" aria-label="Question format">
      <span className="format-bar__label">Format</span>
      <div className="format-bar__chips">
        {FORMAT_OPTIONS.map((o) => {
          const selected = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`format-bar__chip${selected ? " format-bar__chip--selected" : ""}`}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SubjectStep({
  subjects,
  selectedSlug,
  onSelect,
}: {
  subjects: SubjectOption[];
  selectedSlug: string;
  onSelect: (slug: string) => void;
}) {
  const selected = subjects.find((s) => s.slug === selectedSlug) ?? subjects[0];
  if (subjects.length <= 1) {
    // Auto-resolved: tiny static label so the student doesn't have to tap
    // anything to confirm "yes I want AC 401 questions, not French history".
    return (
      <div className="scope-subject scope-subject--locked">
        <span className="scope-subject__label">Subject</span>
        <span className="scope-subject__name">{selected?.name ?? "-"}</span>
      </div>
    );
  }
  // Multi-subject: a real choice. Rendered as a chip row (radiogroup) so
  // a student can switch with one tap.
  return (
    <div className="scope-subject scope-subject--choice" role="radiogroup" aria-label="Subject">
      <span className="scope-subject__label">Subject</span>
      <div className="scope-subject__chips">
        {subjects.map((s) => {
          const isSelected = s.slug === selectedSlug;
          return (
            <button
              key={s.slug}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`scope-subject__chip${isSelected ? " scope-subject__chip--selected" : ""}`}
              onClick={() => onSelect(s.slug)}
            >
              {s.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScopePicker(props: {
  tree: CoverageTree;
  scope: Scope;
  onChange: (s: Scope, meta: ScopeChangeMeta) => void;
  onSearchTyped: (q: string) => void;
}) {
  const { tree, scope, onChange, onSearchTyped } = props;
  const subjects = useSubjects(tree);
  const [selectedSubjectSlug, setSelectedSubjectSlug] = useState(subjects[0]?.slug ?? "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeSuggest, setActiveSuggest] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [expandedTopic, setExpandedTopic] = useState<string | null>(() => {
    if (scope.concept_id) {
      const t = findTopicForConcept(tree, scope.concept_id);
      return t?.slug ?? null;
    }
    if (scope.topic_slug) return scope.topic_slug;
    return null;
  });

  const searchItems = useMemo(() => buildSearchIndex(tree), [tree]);
  const suggestions = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return search(q, searchItems, 10);
  }, [query, searchItems]);
  // Build display rows. Promoted topics emit a group (header + their OWN
  // children only); cross-topic matches that happen to share the query term
  // are NOT inside the "narrow to" list — they go below a labeled section so
  // the student doesn't misread them as further narrowings of the promoted
  // topic. Layout order:
  //   1. all promoted topic groups (each: topic header + its own children)
  //   2. "Also found in other topics" section header (only if step 1 produced anything AND there are leftovers)
  //   3. standalone matches (concepts from other topics, plus any topics that didn't promote)
  // The flat row array doubles as the keyboard-navigation index; the
  // section-header row is non-navigable (skipped by arrow keys).
  const displayRows = useMemo<DisplayRow[]>(() => {
    if (suggestions.length === 0) return [];

    const topicResults = new Map<string, MatchResult>();
    for (const r of suggestions) {
      if (r.item.kind === "topic" && r.item.slug) topicResults.set(r.item.slug, r);
    }
    const childrenByTopic = new Map<string, MatchResult[]>();
    for (const r of suggestions) {
      if (r.item.kind === "concept" && r.item.topic_slug && topicResults.has(r.item.topic_slug)) {
        const arr = childrenByTopic.get(r.item.topic_slug) ?? [];
        arr.push(r);
        childrenByTopic.set(r.item.topic_slug, arr);
      }
    }
    const promotedSlugs: string[] = [];
    for (const [slug, kids] of childrenByTopic) {
      if (kids.length >= 2) promotedSlugs.push(slug);
    }
    // Order promoted groups by their topic's score (the matcher already
    // boosted promoted topics above their children).
    promotedSlugs.sort(
      (a, b) => (topicResults.get(b)?.score ?? 0) - (topicResults.get(a)?.score ?? 0),
    );

    const consumed = new Set<MatchResult>();
    const rows: DisplayRow[] = [];

    for (const slug of promotedSlugs) {
      const topicResult = topicResults.get(slug);
      if (!topicResult) continue;
      rows.push({ kind: "group-topic", item: topicResult.item, groupId: slug });
      consumed.add(topicResult);
      const kids = (childrenByTopic.get(slug) ?? []).sort((a, b) => b.score - a.score);
      for (const k of kids) {
        rows.push({ kind: "group-child", item: k.item, groupId: slug });
        consumed.add(k);
      }
    }

    const leftovers = suggestions.filter((s) => !consumed.has(s));
    if (rows.length > 0 && leftovers.length > 0) {
      rows.push({ kind: "section-header", label: "Also found in other topics" });
    }
    for (const s of leftovers) {
      rows.push({ kind: "standalone", item: s.item });
    }
    return rows;
  }, [suggestions]);

  // Indices in displayRows that are interactive (everything except section-header).
  const navigableIndices = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < displayRows.length; i++) {
      if (displayRows[i].kind !== "section-header") out.push(i);
    }
    return out;
  }, [displayRows]);

  // Reset active suggestion to the first navigable row whenever the result
  // set changes (the first row might be a section-header in some queries).
  useEffect(() => setActiveSuggest(0), [query]);
  useEffect(() => {
    if (navigableIndices.length === 0) return;
    if (!navigableIndices.includes(activeSuggest)) {
      setActiveSuggest(navigableIndices[0]);
    }
  }, [navigableIndices, activeSuggest]);

  // Debounced log of typed queries so we capture intent even when the
  // student doesn't end up selecting from the dropdown.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const timer = setTimeout(() => onSearchTyped(q), 600);
    return () => clearTimeout(timer);
  }, [query, onSearchTyped]);

  const current = describeScope(tree, scope);

  const pickAll = (source: ScopeChangeMeta["source"] = "browse") => {
    onChange({}, { source });
    setOpen(false);
    setQuery("");
  };
  const pickTopic = (slug: string, source: ScopeChangeMeta["source"], q?: string) => {
    onChange({ topic_slug: slug }, { source, query: q });
    setOpen(false);
    setQuery("");
  };
  const pickConcept = (conceptId: string, source: ScopeChangeMeta["source"], q?: string) => {
    onChange({ concept_id: conceptId }, { source, query: q });
    setOpen(false);
    setQuery("");
  };
  const pickItem = (item: SearchItem, source: ScopeChangeMeta["source"], q?: string) => {
    if (item.kind === "topic" && item.slug) pickTopic(item.slug, source, q);
    else if (item.kind === "concept" && item.concept_id) pickConcept(item.concept_id, source, q);
  };
  const toggleExpand = (slug: string) => {
    setExpandedTopic((prev) => (prev === slug ? null : slug));
  };

  const allSelected = !scope.topic_slug && !scope.concept_id;
  const totalQs = tree.topics.reduce((n, t) => n + t.n_questions, 0);

  if (!open) {
    return (
      <div className="scope-bar">
        <button
          type="button"
          className="scope-bar__main"
          onClick={() => {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 30);
          }}
          aria-label="Change practice scope"
          aria-expanded={false}
        >
          <span className="scope-bar__label">Practicing</span>
          <span className="scope-bar__name">
            {current.crumb && <span className="scope-bar__crumb">{current.crumb} › </span>}
            {current.name}
          </span>
        </button>
        {!allSelected && (
          <button
            type="button"
            className="scope-bar__clear"
            onClick={() => pickAll("clear")}
            aria-label="Clear scope back to all topics"
            title="Clear: practice all topics"
          >
            ✕
          </button>
        )}
        <button
          type="button"
          className="scope-bar__action"
          onClick={() => {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 30);
          }}
        >
          Change <span aria-hidden="true">▾</span>
        </button>
      </div>
    );
  }

  return (
    <div className="scope-panel" role="dialog" aria-label="Choose what to practice">
      <div className="scope-panel__header">
        <span className="scope-panel__title">Search what to practice</span>
        <button className="scope-panel__close" onClick={() => setOpen(false)} aria-label="Close">
          Done
        </button>
      </div>

      <SubjectStep
        subjects={subjects}
        selectedSlug={selectedSubjectSlug}
        onSelect={setSelectedSubjectSlug}
      />

      <div className="scope-panel__search">
        <input
          ref={inputRef}
          type="text"
          className="scope-search-input"
          placeholder="Try “breakeven”, “cvp”, “product cost”, “relevant”, “ethics”…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={displayRows.length > 0}
          aria-controls="scope-suggestions"
          aria-activedescendant={
            displayRows.length > 0 ? `scope-sugg-${activeSuggest}` : undefined
          }
          onKeyDown={(e) => {
            if (displayRows.length === 0 || navigableIndices.length === 0) {
              if (e.key === "Escape") setOpen(false);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              const here = navigableIndices.indexOf(activeSuggest);
              const next = Math.min(navigableIndices.length - 1, (here < 0 ? 0 : here + 1));
              setActiveSuggest(navigableIndices[next]);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              const here = navigableIndices.indexOf(activeSuggest);
              const prev = Math.max(0, (here < 0 ? 0 : here - 1));
              setActiveSuggest(navigableIndices[prev]);
            } else if (e.key === "Enter") {
              e.preventDefault();
              const sel = displayRows[activeSuggest];
              if (sel && sel.kind !== "section-header") {
                pickItem(sel.item, "search", query.trim());
              }
            } else if (e.key === "Escape") {
              if (query) setQuery("");
              else setOpen(false);
            }
          }}
        />
        {query && (
          <button
            type="button"
            className="scope-search-clear"
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {query.trim() && (
        <div className="scope-suggestions-wrap">
          {displayRows.length > 0 ? (
            <ul id="scope-suggestions" className="scope-suggestions" role="listbox">
              {displayRows.map((row, i) => {
                if (row.kind === "section-header") {
                  return (
                    <li
                      key={`section:${i}`}
                      role="presentation"
                      className="scope-section-header"
                    >
                      {row.label}
                    </li>
                  );
                }
                const active = i === activeSuggest;
                const key = `${row.kind}:${row.item.slug ?? row.item.concept_id}`;
                const onClick = () => pickItem(row.item, "search", query.trim());
                const onHover = () => setActiveSuggest(i);

                if (row.kind === "group-topic") {
                  return (
                    <li
                      key={key}
                      id={`scope-sugg-${i}`}
                      role="option"
                      aria-selected={active}
                      className="scope-suggestions__group-topic-li"
                    >
                      <button
                        type="button"
                        className={`scope-suggestion scope-suggestion--group-topic${active ? " scope-suggestion--active" : ""}`}
                        onMouseEnter={onHover}
                        onClick={onClick}
                      >
                        <span className="scope-suggestion__tag scope-suggestion__tag--accent">
                          Practice all
                        </span>
                        <span className="scope-suggestion__body">
                          <span className="scope-suggestion__name scope-suggestion__name--lg">
                            {row.item.label}
                          </span>
                          <span className="scope-suggestion__crumb">
                            All {row.item.n_questions} questions in this area
                          </span>
                        </span>
                        <span className="scope-suggestion__chevron" aria-hidden="true">
                          ›
                        </span>
                      </button>
                      <div className="scope-group__narrow-label">Narrow to:</div>
                    </li>
                  );
                }

                if (row.kind === "group-child") {
                  return (
                    <li
                      key={key}
                      id={`scope-sugg-${i}`}
                      role="option"
                      aria-selected={active}
                      className="scope-suggestions__group-child-li"
                    >
                      <button
                        type="button"
                        className={`scope-suggestion scope-suggestion--group-child${active ? " scope-suggestion--active" : ""}`}
                        onMouseEnter={onHover}
                        onClick={onClick}
                      >
                        <span className="scope-suggestion__body">
                          <span className="scope-suggestion__name">{row.item.label}</span>
                        </span>
                        <span className="scope-suggestion__count">{row.item.n_questions} Qs</span>
                      </button>
                    </li>
                  );
                }

                // standalone
                const tag = row.item.kind === "topic" ? "Topic" : "Concept";
                const crumb = row.item.kind === "concept" ? row.item.topic_name : null;
                return (
                  <li
                    key={key}
                    id={`scope-sugg-${i}`}
                    role="option"
                    aria-selected={active}
                  >
                    <button
                      type="button"
                      className={`scope-suggestion${active ? " scope-suggestion--active" : ""}`}
                      onMouseEnter={onHover}
                      onClick={onClick}
                    >
                      <span className="scope-suggestion__tag">{tag}</span>
                      <span className="scope-suggestion__body">
                        <span className="scope-suggestion__name">{row.item.label}</span>
                        {crumb && <span className="scope-suggestion__crumb">{crumb}</span>}
                      </span>
                      <span className="scope-suggestion__count">{row.item.n_questions} Qs</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="scope-no-match">
              <strong>No match.</strong> Try <em>“breakeven”</em>, <em>“cvp”</em>,{" "}
              <em>“product cost”</em>, <em>“relevant”</em>, <em>“ethics”</em>, or browse the
              topics below.
            </div>
          )}
        </div>
      )}

      <div className="scope-divider">
        <span>or browse all topics</span>
      </div>

      <ul className="scope-panel__list">
        <li className="scope-panel__group">
          <button
            type="button"
            className="scope-row scope-row--all"
            aria-pressed={allSelected}
            onClick={() => pickAll("browse")}
          >
            <span className="scope-row__radio" aria-hidden="true" />
            <span className="scope-row__name">All topics</span>
            <span className="scope-row__count">{totalQs}</span>
            <span style={{ width: 36 }} aria-hidden="true" />
          </button>
        </li>
        {tree.topics.map((t) => {
          const topicSelected = scope.topic_slug === t.slug;
          const hasSelectedConcept =
            !!scope.concept_id && t.concepts.some((c) => c.concept_id === scope.concept_id);
          const expanded = expandedTopic === t.slug;
          return (
            <li key={t.slug} className="scope-panel__group">
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <button
                  type="button"
                  className="scope-row"
                  aria-pressed={topicSelected || hasSelectedConcept}
                  onClick={() => pickTopic(t.slug, "browse")}
                  style={{ flex: 1 }}
                >
                  <span className="scope-row__radio" aria-hidden="true" />
                  <span className="scope-row__name">{t.name}</span>
                  <span className="scope-row__count">{t.n_questions}</span>
                </button>
                <button
                  type="button"
                  className="scope-row__expand"
                  onClick={() => toggleExpand(t.slug)}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? "Hide" : "Show"} concepts in ${t.name}`}
                  style={{ alignSelf: "center", marginRight: 6 }}
                >
                  {expanded ? "▴" : "▾"}
                </button>
              </div>
              {expanded && (
                <ul className="scope-panel__concepts">
                  {t.concepts.map((c) => {
                    const selected = scope.concept_id === c.concept_id;
                    return (
                      <li key={c.concept_id}>
                        <button
                          type="button"
                          className="scope-row scope-row--concept"
                          aria-pressed={selected}
                          onClick={() => pickConcept(c.concept_id, "browse")}
                        >
                          <span className="scope-row__radio" aria-hidden="true" />
                          <span className="scope-row__name">{c.name}</span>
                          <span className="scope-row__count">{c.n_questions}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Exported so the admin "Try this question" preview on AdminQuestionDetail
// can compose answers exactly the way Practice does — same JSON-array
// shape for parts, same letter shape for MC. Keeps the preview's wire
// payload identical to the live student path so what an admin sees
// matches what a student submits.
export function composeAnswer(
  rf: ServedQuestion["response_format"],
  parsedParts: ParsedMultiPart | null,
  s: { draft: string; chosenLetter: string | null; partAnswers: string[] },
  hasStructuredParts: boolean = false
): string {
  if (rf === "multiple_choice") return s.chosenLetter ?? "";
  if (rf === "multi_part") {
    if (hasStructuredParts && parsedParts && parsedParts.parts.length > 0) {
      // Flavor 2: POST a JSON array of per-part strings, in order. The
      // backend's parts-aware grader (backend/practice._grade_parts)
      // detects the JSON-array shape and routes to per-part grading.
      const arr = parsedParts.parts.map((_, i) => s.partAnswers[i] ?? "");
      return JSON.stringify(arr);
    }
    if (parsedParts && parsedParts.parts.length > 0) {
      // Legacy structured-from-markers path: "(a) <ans>; (b) <ans>; ..." —
      // the shape the model grader has always seen.
      return composeMultiPartAnswer(parsedParts.parts, s.partAnswers);
    }
    // Unstructured fallback (questions without (a)/(b)/(c)/(d) markers in
    // the stem): preserve the prior behavior — single textarea contents
    // joined as-is.
    return s.partAnswers.join(" ");
  }
  return s.draft;
}

export function canCommit(
  rf: ServedQuestion["response_format"],
  parsedParts: ParsedMultiPart | null,
  s: { draft: string; chosenLetter: string | null; partAnswers: string[] }
): boolean {
  if (rf === "multiple_choice") return !!s.chosenLetter;
  if (rf === "multi_part") {
    if (parsedParts && parsedParts.parts.length > 0) {
      return multiPartReady(parsedParts.parts, s.partAnswers);
    }
    return s.partAnswers.some((p) => p.trim());
  }
  return s.draft.trim().length > 0;
}

export function AnswerInput(props: {
  q: ServedQuestion;
  parsedParts: ParsedMultiPart | null;
  disabled: boolean;
  draft: string;
  setDraft: (v: string) => void;
  chosenLetter: string | null;
  setChosenLetter: (v: string | null) => void;
  partAnswers: string[];
  setPartAnswers: (v: string[]) => void;
  graded: AnswerResult | null;
}) {
  const { q, parsedParts, disabled, draft, setDraft, chosenLetter, setChosenLetter, partAnswers, setPartAnswers, graded } = props;

  if (q.response_format === "multiple_choice" && q.choices) {
    const correctLetter = graded ? extractLetter(graded.answer_key) : null;
    return (
      <div className="choices">
        {q.choices.map((c, i) => {
          const letter = String.fromCharCode(65 + i);
          // Defensive: if a choice ever arrives as something other than a string
          // (it shouldn't, per the bank schema), coerce so .replace doesn't throw.
          const cs = typeof c === "string" ? c : String(c);
          const text = cs.replace(/^[A-D]\)\s*/, "");
          let state: "correct" | "incorrect" | null = null;
          if (graded) {
            if (letter === correctLetter) state = "correct";
            else if (chosenLetter === letter) state = "incorrect";
          }
          return (
            <button
              key={letter}
              type="button"
              className="choice"
              aria-pressed={chosenLetter === letter}
              data-state={state ?? undefined}
              disabled={disabled}
              onClick={() => setChosenLetter(letter)}
            >
              <span className="letter">{letter})</span>
              <span>{text}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (q.response_format === "single_numeric") {
    return (
      <input
        className="input numeric-input"
        inputMode="decimal"
        placeholder="Your numeric answer (units, $, %, etc.)"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
      />
    );
  }

  if (q.response_format === "multi_part") {
    // STRUCTURED PATH — the stem carries (a)/(b)/(c)/(d) markers.
    // Render one labeled input per part with a type-appropriate widget.
    if (parsedParts && parsedParts.parts.length > 0) {
      return (
        <div className="multi-part stack">
          {parsedParts.parts.map((part, i) => {
            const value = partAnswers[i] ?? "";
            const update = (v: string) => {
              const next = partAnswers.slice();
              while (next.length <= i) next.push("");
              next[i] = v;
              setPartAnswers(next);
            };
            return (
              <div key={`${part.letter}-${i}`} className="multi-part__item">
                <div className="multi-part__header">
                  <span className="multi-part__letter">({part.letter})</span>
                  <span className="multi-part__prompt">{part.prompt}</span>
                </div>
                {part.type.kind === "binary" || part.type.kind === "choice" ? (
                  <div className="multi-part__binary">
                    {part.type.options.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className="multi-part__binary-btn"
                        aria-pressed={value === opt}
                        data-state={value === opt ? "selected" : undefined}
                        disabled={disabled}
                        onClick={() => update(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : part.type.kind === "numeric" ? (
                  <input
                    className="input multi-part__input"
                    inputMode="decimal"
                    placeholder="Your numeric answer"
                    value={value}
                    onChange={(e) => update(e.target.value)}
                    disabled={disabled}
                    aria-label={`Part ${part.letter}`}
                  />
                ) : (
                  <input
                    className="input multi-part__input"
                    type="text"
                    placeholder="Your answer for this part"
                    value={value}
                    onChange={(e) => update(e.target.value)}
                    disabled={disabled}
                    aria-label={`Part ${part.letter}`}
                  />
                )}
                {part.confidence === "inferred" && (
                  <p className="multi-part__hint muted">
                    Input type inferred from the prompt. If the part wants
                    something else, you can still type the right shape.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // FALLBACK — the stem doesn't carry parseable (a)/(b)/(c)/(d) markers.
    // Keep the prior single-textarea behavior so we don't regress anything.
    return (
      <div className="stack">
        <textarea
          className="input"
          rows={3}
          placeholder="Your answer (multi-part: separate parts with semicolons or new lines)"
          value={partAnswers[0] ?? ""}
          onChange={(e) => setPartAnswers([e.target.value])}
          disabled={disabled}
        />
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Tip: include each numeric value the question asks for.
        </p>
      </div>
    );
  }

  // free_text
  return (
    <textarea
      className="input"
      rows={5}
      placeholder="Write your response…"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      disabled={disabled}
    />
  );
}

function extractLetter(s: string): string | null {
  const m = /^([A-D])/.exec(s.trim().toUpperCase());
  return m ? m[1] : null;
}

interface NormalizedRationale {
  label: string;                  // "A", "B", "1", … — display key
  why_picked?: string;
  why_wrong?: string;
  raw?: string;                   // fallback for legacy string shapes
}

function normalizeRationales(dr: DistractorRationales): NormalizedRationale[] {
  if (dr === null || dr === undefined) return [];
  if (Array.isArray(dr)) {
    return dr.map((entry, i) => {
      const label = String.fromCharCode(65 + i); // A, B, C, …
      if (typeof entry === "string") return { label, raw: entry };
      // Defensive: an array of rich-rationale objects.
      const r = entry as RichRationale;
      return { label, why_picked: r.why_picked, why_wrong: r.why_wrong };
    });
  }
  return Object.entries(dr).map(([k, v]) => {
    if (typeof v === "string") return { label: k, raw: v };
    const r = (v as RichRationale) ?? {};
    return { label: k, why_picked: r.why_picked, why_wrong: r.why_wrong };
  });
}

export function Solution({ result, q }: { result: AnswerResult; q: ServedQuestion }) {
  const rationales = normalizeRationales(result.distractor_rationales);
  return (
    <div className="stack">
      <div className="solution">
        <h4>Answer</h4>
        <div style={{ marginBottom: 14, fontWeight: 500 }}>{String(result.answer_key ?? "")}</div>
        <h4>Worked solution</h4>
        <div>
          <WorkedSolutionText text={String(result.worked_solution ?? "")} />
        </div>
      </div>
      {result.part_results && result.part_results.length > 0 && (
        <div className="solution">
          <h4>Per-part feedback</h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {result.part_results.map((pr) => (
              <li key={pr.label} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ({pr.label}){" "}
                  <span
                    data-state={pr.correct === true ? "correct" : pr.correct === false ? "incorrect" : undefined}
                    style={{
                      fontWeight: 500,
                      color: pr.correct === true ? "var(--ok, #1f7a3a)" :
                             pr.correct === false ? "var(--bad, #b3261e)" :
                             "var(--muted, #666)",
                    }}
                  >
                    {pr.correct === true ? "Correct" :
                     pr.correct === false ? "Incorrect" : "Pending review"}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 13, marginBottom: 2 }}>
                  Your answer: <code>{pr.student_answer || "—"}</code>
                </div>
                {pr.correct === false && (
                  <div className="muted" style={{ fontSize: 13, marginBottom: 2 }}>
                    Expected: <code>{pr.answer}</code>
                  </div>
                )}
                {pr.explanation && (
                  <div style={{ fontSize: 13 }}>{pr.explanation}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {q.response_format === "multiple_choice" && rationales.length > 0 && (
        <div className="solution">
          <h4>Why the other options miss</h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {rationales.map((r) => (
              <li key={r.label} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.label})</div>
                {r.raw ? (
                  <div>{r.raw}</div>
                ) : (
                  <>
                    {r.why_picked && (
                      <div style={{ marginBottom: 4 }}>
                        <span className="muted" style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          Why a student might pick it
                        </span>
                        <div>{r.why_picked}</div>
                      </div>
                    )}
                    {r.why_wrong && (
                      <div>
                        <span className="muted" style={{ fontSize: 12, fontWeight: 500, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          Why it&rsquo;s wrong
                        </span>
                        <div>{r.why_wrong}</div>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
